import { showDialog } from '@adornis/dialog/dialog-functions.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import type { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import type { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';

interface ValidationErrorDialogProps {
  /**
   * The errors for the dialog. Can handle single and multiple
   */
  error: ValidationError | MultipleValidationError;

  /**
   * Should the Dialog hard validate the form. In this case it will only ever return false, as the user cannot continue.
   * Defaults to false.
   */
  hardValidate?: boolean;

  /**
   * Additional info text for the controls. Renders directly above the buttons at the end of the dialog
   */
  infoRenderable?: Renderable;

  /**
   * Return text. The text for the buttons which rejects and continues editing.
   * Defaults to "Weiter bearbeiten".
   */
  rejectText?: string;

  /**
   * Return text. The text for the buttons which accepts and continues. Only visible if hardValidate is false.
   * Defaults to "Trotzdem fortfahren".
   */
  acceptText?: string;
}

const ValidationErrorDialogContent = stateful(
  (props: { resolve: (shouldContinue: boolean) => void } & ValidationErrorDialogProps) => {
    // Depending on if the validationErrors array exists we might we have multiple errors
    const errorArray = props.error.extensions.validationErrors ? props.error.extensions.validationErrors : null;
    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: errorArray
            ? 'Es wurden mehrere Fehler im Formular gefunden'
            : 'Es wurde ein Fehler im Formular gefunden',
        })}

        <ul class="list">
          ${
            errorArray
              ? html`${errorArray.map(
                  (e, idx) => html`
                    <li class="list-row items-center">
                      <div class="text-2xl font-semibold tabular-nums text-error">${idx + 1}</div>
                      ${e.message}
                    </li>
                  `,
                )}`
              : html`
                  <li class="list-row items-center">
                    <div class="text-2xl font-semibold tabular-nums text-error">1</div>
                    ${props.error.message}
                  </li>
                `
          }
        </ul>
        ${
          props.hardValidate
            ? html`
                <button class="btn btn-primary btn-lg" @click=${() => props.resolve(false)}>
                  ${props.rejectText || 'Weiter bearbeiten'}
                </button>
              `
            : html`
                <div class="divider my-0"></div>
                <p>
                  Möchten Sie trotzdem fortfahren? <br />
                  Sie können jederzeit zu diesem Schritt zurückkehren. Sofern das Formular <b>nicht vollständig</b>
                  abgeschlossen wurde.
                </p>
                <div class="flex flex-row justify-between gap-4">
                  <button class="btn btn-primary btn-lg" @click=${() => props.resolve(false)}>
                    ${props.rejectText || 'Weiter bearbeiten'}
                  </button>
                  <button class="btn btn-lg btn-ghost" @click=${() => props.resolve(true)}>
                    ${props.acceptText || 'Trotzdem fortfahren'}
                  </button>
                </div>
              `
        }
      </div>
    `;
  },
);

/**
 * A dialog to show validation errors and get user confirmation to continue or not.
 * @param options ValidationErrorDialogProps
 * @returns true if the user decided to save anyway, false if he wants to continue editing. Is always false if hardValidate is true.
 */
export async function openValidationErrorDialog(props: ValidationErrorDialogProps) {
  const shouldContinue = await showDialog<boolean>({
    content: (resolve: (shouldContinue: boolean) => void) => ValidationErrorDialogContent({ ...props, resolve }),
    modal: true,
  });

  return shouldContinue;
}
